package org.teddapp.project.ui

import androidx.compose.ui.text.style.TextOverflow

import androidx.compose.foundation.layout.fillMaxWidth

import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.scale
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Column

/** Un onglet de [TeddTabs]. */
data class TeddTab(val id: String, val label: String, val icon: String? = null, val count: Int? = null)

/**
 * Onglets segmentés : une piste en retrait, l'onglet choisi posé dessus sur la surface.
 * Pour basculer entre des vues d'un même contenu, pas pour naviguer entre modules.
 */
@Composable
fun TeddTabs(
    tabs: List<TeddTab>,
    selected: String,
    onSelect: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier
            .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm + 3.dp))
            .padding(3.dp),
        horizontalArrangement = Arrangement.spacedBy(2.dp),
    ) {
        tabs.forEach { tab ->
            val active = tab.id == selected
            val shape = RoundedCornerShape(TeddTokens.RSm)
            Row(
                modifier = Modifier
                    .then(if (active) Modifier.teddCardShadow(shape) else Modifier)
                    .teddClickable(
                        onClick = { onSelect(tab.id) },
                        shape = shape,
                        fill = if (active) TeddTokens.Surface else Color.Transparent,
                        hover = if (active) TeddTokens.Surface else Color.Transparent,
                        role = Role.Tab,
                    )
                    .height(TeddSize.ControlSm)
                    .padding(horizontal = TeddSpace.S3),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                val color = if (active) TeddTokens.Ink else TeddTokens.InkMuted
                if (tab.icon != null) TeddIcon(name = tab.icon, size = 15.dp, color = color)
                Text(tab.label, color = color, style = TeddType.Label, maxLines = 1)
                if (tab.count != null) {
                    Text(tab.count.toString(), color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp))
                }
            }
        }
    }
}

/**
 * Case à cocher. Cochée : remplissage citron vert et coche d'encre, qui rebondit
 * légèrement — le seul moment de plaisir assumé du système. [round] pour les habitudes.
 */
@Composable
fun TeddCheckbox(
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    modifier: Modifier = Modifier,
    round: Boolean = false,
    enabled: Boolean = true,
) {
    val shape = if (round) CircleShape else RoundedCornerShape(TeddTokens.RXs)
    val mark by animateFloatAsState(
        targetValue = if (checked) 1f else 0f,
        animationSpec = spring(dampingRatio = Spring.DampingRatioMediumBouncy, stiffness = Spring.StiffnessMedium),
    )
    val onLime = TeddTokens.OnLime
    Box(
        modifier = modifier
            .size(20.dp)
            .teddClickable(
                onClick = { onCheckedChange(!checked) },
                shape = shape,
                fill = if (checked) TeddTokens.Lime else TeddTokens.Surface,
                hover = if (checked) TeddTokens.Lime else TeddTokens.SurfaceSunken,
                enabled = enabled,
                role = Role.Checkbox,
            )
            .border(1.5.dp, if (checked) TeddTokens.LimeStrong else TeddTokens.LineStrong, shape),
        contentAlignment = Alignment.Center,
    ) {
        if (mark > 0.01f) {
            Canvas(Modifier.size(20.dp)) {
                val unit = size.minDimension / 24f
                val path = Path().apply {
                    moveTo(6.5f * unit, 12.5f * unit)
                    lineTo(10.5f * unit, 16.5f * unit)
                    lineTo(17.5f * unit, 8.5f * unit)
                }
                scale(mark, Offset(size.width / 2, size.height / 2)) {
                    drawPath(path, onLime, style = Stroke(2.dp.toPx(), cap = StrokeCap.Round, join = StrokeJoin.Round))
                }
            }
        }
    }
}

/** Case à cocher suivie de son libellé. */
@Composable
fun TeddCheckboxRow(
    label: String,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    modifier: Modifier = Modifier,
    round: Boolean = false,
) {
    Row(modifier, verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
        TeddCheckbox(checked, onCheckedChange, round = round)
        Text(label, color = TeddTokens.Ink, style = TeddType.Body, fontWeight = FontWeight(400))
    }
}

/**
 * Ligne d'une liste de filtres ou d'une arborescence (catégories, projets, carnets).
 * Choisie, elle se pose sur la surface et son compteur passe sur citron vert — le même
 * langage que la navigation principale. [leading] porte l'icône ou la pastille.
 */
@Composable
fun TeddFilterRow(
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    count: Int? = null,
    leading: @Composable () -> Unit = {},
) {
    val shape = RoundedCornerShape(TeddTokens.RSm)
    Row(
        modifier = modifier
            .fillMaxWidth()
            .then(if (selected) Modifier.teddCardShadow(shape) else Modifier)
            .teddClickable(
                onClick = onClick,
                shape = shape,
                fill = if (selected) TeddTokens.Surface else Color.Transparent,
                hover = if (selected) TeddTokens.Surface else TeddTokens.SurfaceSunken,
                role = null,
            )
            .height(36.dp)
            .padding(horizontal = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        leading()
        Text(
            text = label,
            modifier = Modifier.weight(1f),
            color = if (selected) TeddTokens.Ink else TeddTokens.InkMuted,
            style = TeddType.Label.copy(fontSize = 14.sp, fontWeight = FontWeight(if (selected) 540 else 400)),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
        if (count != null) {
            Text(
                text = "$count",
                modifier = if (selected) {
                    Modifier.background(TeddTokens.Lime, RoundedCornerShape(TeddTokens.RFull)).padding(horizontal = 6.dp, vertical = 1.dp)
                } else Modifier,
                color = if (selected) TeddTokens.OnLime else TeddTokens.InkMuted,
                style = TeddType.MonoSm.copy(fontSize = 11.sp),
            )
        }
    }
}

/**
 * Pastille de filtre (carnets, catégories, étiquettes, années). Celle qui est choisie se
 * pose sur la surface, cerclée d'encre ; les autres restent en contour. Une couleur de
 * donnée (catégorie, colonne) passe par [dotColor], jamais par le fond.
 */
@Composable
fun TeddFilterChip(
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    count: Int? = null,
    icon: String? = null,
    dotColor: Color? = null,
) {
    val shape = RoundedCornerShape(TeddTokens.RFull)
    Row(
        modifier = modifier
            .border(1.dp, if (selected) TeddTokens.Ink else TeddTokens.Line, shape)
            .teddClickable(onClick = onClick, shape = shape, fill = if (selected) TeddTokens.Surface else Color.Transparent)
            .height(34.dp)
            .padding(horizontal = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        if (dotColor != null) Box(Modifier.size(8.dp).background(dotColor, CircleShape))
        if (icon != null) TeddIcon(name = icon, size = 14.dp, color = if (selected) TeddTokens.Ink else TeddTokens.InkMuted)
        Text(
            text = label,
            color = if (selected) TeddTokens.Ink else TeddTokens.InkMuted,
            style = TeddType.Label.copy(fontWeight = FontWeight(if (selected) 600 else 450)),
            maxLines = 1,
        )
        if (count != null) Text("$count", color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp))
    }
}

/**
 * Carte d'une liste sur téléphone : une surface bordée, toute entière cliquable. Sur un
 * petit écran, chaque élément d'une liste est une carte : la cible est franche sous le pouce.
 */
@Composable
fun TeddListCard(
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    selected: Boolean = false,
    content: @Composable ColumnScope.() -> Unit,
) {
    val shape = RoundedCornerShape(TeddTokens.RMd)
    Column(
        modifier = modifier
            .fillMaxWidth()
            .border(1.dp, if (selected) TeddTokens.Ink else TeddTokens.Line, shape)
            .teddClickable(onClick = onClick, shape = shape, fill = TeddTokens.Surface, hover = TeddTokens.Surface, role = null)
            .padding(horizontal = TeddSpace.S4, vertical = TeddSpace.S3),
        content = content,
    )
}

/** Champ de recherche : une loupe, le texte, rien d'autre. */
@Composable
fun TeddSearchField(value: String, onValueChange: (String) -> Unit, placeholder: String, modifier: Modifier = Modifier) {
    val shape = RoundedCornerShape(TeddTokens.RSm)
    Row(
        modifier = modifier
            .fillMaxWidth()
            .height(TeddSize.ControlMd)
            .background(TeddTokens.Surface, shape)
            .border(1.dp, TeddTokens.LineStrong, shape)
            .padding(horizontal = TeddSpace.S3),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
    ) {
        TeddIcon(name = "search", size = 16.dp, color = TeddTokens.InkMuted)
        androidx.compose.foundation.text.BasicTextField(
            value = value,
            onValueChange = onValueChange,
            singleLine = true,
            modifier = Modifier.weight(1f),
            textStyle = TeddType.Body.copy(color = TeddTokens.Ink),
            cursorBrush = androidx.compose.ui.graphics.SolidColor(TeddTokens.Ink),
            decorationBox = { inner ->
                if (value.isEmpty()) Text(placeholder, color = TeddTokens.InkSubtle, style = TeddType.Body, maxLines = 1)
                inner()
            },
        )
        if (value.isNotEmpty()) {
            TeddIcon(
                name = "x",
                size = 16.dp,
                color = TeddTokens.InkMuted,
                modifier = Modifier.teddClickable(onClick = { onValueChange("") }, shape = CircleShape),
            )
        }
    }
}

/** Bouton flottant du téléphone : le geste principal de l'écran, sous le pouce. */
@Composable
fun TeddFab(icon: String, onClick: () -> Unit, modifier: Modifier = Modifier) {
    Box(
        modifier = modifier
            .size(56.dp)
            .teddOverlayShadow(CircleShape)
            .teddClickable(onClick = onClick, shape = CircleShape, fill = TeddTokens.Action, hover = TeddTokens.Action),
        contentAlignment = Alignment.Center,
    ) {
        TeddIcon(name = icon, size = 22.dp, color = TeddTokens.OnAction, strokeWidth = 2.dp)
    }
}
